import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react'
import { supabase } from '../supabaseClient'
import { useTheme } from '../ThemeContext'
import { useAuth } from '../AuthContext'
import { useOrganization } from '../hooks/useOrganization'
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api'
import { MAPS_LIBRARIES, MAPS_API_KEY } from '../mapsConfig'

const InspectionForm = lazy(() => import('./InspectionForm'))

const DAY_MS = 86400000

function daysSince(dateStr) {
  if (!dateStr) return null
  const [y, m, d] = dateStr.split('-').map(Number)
  const then = new Date(y, m - 1, d)
  const now = new Date(); now.setHours(0, 0, 0, 0)
  return Math.floor((now - then) / DAY_MS)
}

function getStatus(days) {
  if (days === null) return 'never'
  if (days <= 180) return 'current'
  if (days <= 365) return 'dueSoon'
  return 'overdue'
}

function formatDate(dateStr) {
  if (!dateStr) return '—'
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}


function useAnimatedCounter(target, duration = 700) {
  const [value, setValue] = useState(0)
  const raf = useRef(null)
  useEffect(() => {
    if (target === 0) { setValue(0); return }
    const start = Date.now()
    const tick = () => {
      const progress = Math.min((Date.now() - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(eased * target))
      if (progress < 1) raf.current = requestAnimationFrame(tick)
    }
    raf.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf.current)
  }, [target, duration])
  return value
}

function TerritoryMap({ establishments, C, darkMode, onNavigate, mapHeight = 240 }) {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: MAPS_API_KEY, libraries: MAPS_LIBRARIES })
  const [selectedMarker, setSelectedMarker] = useState(null)
  const mapRef = useRef(null)

  const mappable = establishments.filter(e => e.latitude && e.longitude)

  const onLoad = useCallback((map) => {
    mapRef.current = map
    if (mappable.length === 0) return
    const bounds = new window.google.maps.LatLngBounds()
    mappable.forEach(e => bounds.extend({ lat: e.latitude, lng: e.longitude }))
    map.fitBounds(bounds, 48)
  }, [mappable])

  const statusColor = (status) => {
    if (status === 'overdue') return '#ff5f6d'
    if (status === 'dueSoon') return '#ffbd4a'
    if (status === 'current') return '#37d67a'
    return '#5f7893'
  }

  const makeIcon = (color) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26"><path d="M13 2C8.6 2 5 5.6 5 10c0 6 8 14 8 14s8-8 8-14c0-4.4-3.6-8-8-8z" fill="${color}" stroke="white" stroke-width="1.5"/><circle cx="13" cy="10" r="3" fill="white"/></svg>`
    const icon = { url: `data:image/svg+xml,${encodeURIComponent(svg)}` }
    if (window.google?.maps?.Size) {
      icon.scaledSize = new window.google.maps.Size(26, 26)
      icon.anchor = new window.google.maps.Point(13, 26)
    }
    return icon
  }

  const darkStyle = [
    { elementType: 'geometry', stylers: [{ color: '#0f1e35' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#0a1628' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#5f7893' }] },
    { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#1a2a4a' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#162540' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1a2a4a' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#4a6a8a' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0a1628' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  ]

  if (!isLoaded) {
    return (
      <div style={{ height: 240, background: C.surface2, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.dim, fontSize: 13 }}>
        Loading map…
      </div>
    )
  }

  return (
    <div style={{ position: 'relative', height: mapHeight }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%', borderRadius: 0 }}
        options={{ styles: darkMode ? darkStyle : [], disableDefaultUI: true, zoomControl: true, clickableIcons: false, gestureHandling: 'cooperative' }}
        onLoad={onLoad}
        center={mappable.length > 0 ? { lat: mappable[0].latitude, lng: mappable[0].longitude } : { lat: 35.7796, lng: -78.6382 }}
        zoom={11}
      >
        {mappable.map(est => (
          <Marker
            key={est.id}
            position={{ lat: est.latitude, lng: est.longitude }}
            icon={makeIcon(statusColor(est.status))}
            onClick={() => setSelectedMarker(est)}
          />
        ))}
        {selectedMarker && (
          <InfoWindow
            position={{ lat: selectedMarker.latitude, lng: selectedMarker.longitude }}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <div style={{ fontFamily: 'Inter, sans-serif', minWidth: 120 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#102033' }}>{selectedMarker.trade_name}</div>
              <div style={{ fontSize: 11, color: '#506b87', marginTop: 2 }}>{selectedMarker.mxb_number}</div>
              <div style={{ fontSize: 11, marginTop: 4, fontWeight: 600, color: statusColor(selectedMarker.status), textTransform: 'capitalize' }}>{selectedMarker.status === 'dueSoon' ? 'Due Soon' : selectedMarker.status}</div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
      {mappable.length === 0 && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.dim, fontSize: 13, pointerEvents: 'none' }}>
          No geocoded establishments to display
        </div>
      )}
      <div style={{ position: 'absolute', bottom: 10, left: 10, display: 'flex', gap: 8, pointerEvents: 'none' }}>
        {[['#ff5f6d', 'Overdue'], ['#ffbd4a', 'Due Soon'], ['#37d67a', 'Current']].map(([color, label]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, background: darkMode ? 'rgba(10,22,40,0.82)' : 'rgba(255,255,255,0.88)', backdropFilter: 'blur(6px)', borderRadius: 6, padding: '4px 8px', border: `1px solid ${C.border}` }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
            <span style={{ fontSize: 11, fontWeight: 500, color: C.muted }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function AgentDashboard({ onNavigate, isSuperAdmin, userRole }) {
  const { darkMode } = useTheme()
  const { user } = useAuth()
  const { organization } = useOrganization()

  const [liveOps, setLiveOps] = useState({ myTotal: 0, myCompliance: 0, agencyCompliance: 0, openComplaints: 0 })
  const [statCards, setStatCards] = useState({ current: 0, dueSoon: 0, overdue: 0, myMonth: 0 })
  const [attentionQueue, setAttentionQueue] = useState([])
  const [mapEstablishments, setMapEstablishments] = useState([])
  const [recentActivity, setRecentActivity] = useState([])
  const [complaints, setComplaints] = useState([])
  const [larcenyReports, setLarcenyReports] = useState([])
  const [onCallAgent, setOnCallAgent] = useState(null)
  const [followups, setFollowups] = useState([])
  const [showFollowups, setShowFollowups] = useState(false)
  const [, setLastNote] = useState([])
  const [loading, setLoading] = useState(true)
  const [inspectingEst, setInspectingEst] = useState(null)

  const fetchDashboard = useCallback(async () => {
    if (!user || !organization) return
    try {

      // Resolve users.id (may differ from auth UUID)
      let profileId = user.id
      const { data: profileData } = await supabase
        .from('users')
        .select('id')
        .eq('email', user.email)
        .single()
      if (profileData?.id) profileId = profileData.id

      const allEsts = []
      let offset = 0
      while (true) {
        let q = supabase
          .from('establishments')
          .select('id, mxb_number, trade_name, assigned_inspector_id, latitude, longitude')
          .eq('active', true)
          .eq('establishment_type', 'mxb')
          .range(offset, offset + 999)
        if (organization?.id) q = q.eq('organization_id', organization.id)
        const { data } = await q
        if (!data || data.length === 0) break
        allEsts.push(...data)
        if (data.length < 1000) break
        offset += 1000
      }

      const lastMap = {}
      const estIds = allEsts.map(e => e.id)
      for (let i = 0; i < estIds.length; i += 500) {
        const batch = estIds.slice(i, i + 500)
        const { data: dates } = await supabase.rpc('get_last_inspection_dates', { est_ids: batch })
        if (dates) dates.forEach(r => { lastMap[r.establishment_id] = r.inspection_date })
      }

      const withStatus = allEsts.map(est => {
        const lastDate = lastMap[est.id] || null
        const days = daysSince(lastDate)
        return { ...est, lastDate, days, status: getStatus(days) }
      })

      const myEsts = isSuperAdmin
        ? withStatus
        : withStatus.filter(e => e.assigned_inspector_id === profileId)

      const current  = myEsts.filter(e => e.status === 'current').length
      const dueSoon  = myEsts.filter(e => e.status === 'dueSoon').length
      const overdue  = myEsts.filter(e => e.status === 'overdue').length

      const agencyCompliant  = withStatus.filter(e => e.days !== null && e.days <= 365).length
      const agencyCompliance = withStatus.length > 0 ? Math.round((agencyCompliant / withStatus.length) * 100) : 0
      const myCompliant      = myEsts.filter(e => e.days !== null && e.days <= 365).length
      const myCompliance     = myEsts.length > 0 ? Math.round((myCompliant / myEsts.length) * 100) : 0

      const now = new Date()
      const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
      let myMonth = 0
      if (isSuperAdmin) {
        // Scope by org user IDs to count all inspections regardless of establishment type/active status
        const { data: orgUsers } = await supabase.from('users').select('id').eq('organization_id', organization.id)
        const orgUserIds = (orgUsers || []).map(u => u.id)
        for (let i = 0; i < orgUserIds.length; i += 500) {
          const { count } = await supabase.from('inspections').select('id', { count: 'exact', head: true }).gte('inspection_date', monthStart).in('inspector_id', orgUserIds.slice(i, i + 500))
          myMonth += count || 0
        }
      } else {
        const { count } = await supabase.from('inspections').select('id', { count: 'exact', head: true }).gte('inspection_date', monthStart).eq('inspector_id', profileId)
        myMonth = count || 0
      }

      let compCountQ = supabase.from('complaints').select('id', { count: 'exact', head: true }).not('status', 'in', '("closed","inactive")').eq('organization_id', organization.id)
      if (!isSuperAdmin) compCountQ = compCountQ.eq('assigned_to_id', profileId)
      const { count: openComplaints } = await compCountQ

      const urgentEsts = myEsts
        .filter(e => e.status === 'overdue' || e.status === 'dueSoon')
        .sort((a, b) => {
          if (a.status === 'overdue' && b.status !== 'overdue') return -1
          if (b.status === 'overdue' && a.status !== 'overdue') return 1
          if (a.status === 'overdue') return (b.days || 0) - (a.days || 0)
          return (a.days || 0) - (b.days || 0)
        })
      const urgentIds = new Set(urgentEsts.map(e => e.id))
      const fillerEsts = myEsts
        .filter(e => !urgentIds.has(e.id))
        .sort((a, b) => (b.days || 0) - (a.days || 0))
      const queue = [...urgentEsts, ...fillerEsts].slice(0, 10)

      let recentQ = supabase
        .from('inspections')
        .select(`id, inspection_date, disposition, establishments(trade_name, mxb_number), users:inspector_id(first_name, last_name)`)
        .eq('organization_id', organization.id)
        .order('inspection_date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(5)
      const { data: recentData } = await recentQ

      let myCompQ = supabase
        .from('complaints')
        .select(`id, date_received, complaint_description, status, establishments(trade_name, mxb_number)`)
        .not('status', 'in', '("closed","inactive")')
        .eq('organization_id', organization.id)
        .order('date_received', { ascending: false })
      if (!isSuperAdmin) myCompQ = myCompQ.eq('assigned_to_id', profileId)
      const { data: compData } = await myCompQ

      const { data: onCallData } = await supabase
        .from('users')
        .select('id, first_name, last_name, phone_number, on_call_since')
        .eq('organization_id', organization.id)
        .eq('is_on_call', true)
        .single()
      setOnCallAgent(onCallData || null)

      let followupQ = supabase
        .from('inspections')
        .select(`id, followup_date, followup_notes, establishments(trade_name, mxb_number)`)
        .eq('followup_required', true)
        .eq('organization_id', organization.id)
        .order('followup_date', { ascending: true })
      if (!isSuperAdmin) followupQ = followupQ.eq('inspector_id', profileId)
      const { data: followupData } = await followupQ
      setFollowups(followupData || [])

      const { data: noteData } = await supabase
        .from('agent_notes')
        .select('id, content, created_at')
        .eq('organization_id', organization.id)
        .eq('user_id', profileId)
        .order('created_at', { ascending: false })
        .limit(5)
      setLastNote(noteData || [])

      let larcenyData = []
      if (organization?.features?.larceny_reports) {
        const { data } = await supabase
          .from('larceny_reports')
          .select('id, created_at, status, abc_stores(store_number)')
          .eq('organization_id', organization.id)
          .order('created_at', { ascending: false })
          .limit(5)
        larcenyData = data || []
      }
      setLarcenyReports(larcenyData)

      setLiveOps({ myTotal: myEsts.length, myCompliance, agencyCompliance, openComplaints: openComplaints || 0 })
      setStatCards({ current, dueSoon, overdue, myMonth: myMonth || 0 })
      setAttentionQueue(queue)
      setMapEstablishments(myEsts.filter(e => e.latitude && e.longitude))
      setRecentActivity(recentData || [])
      setComplaints(compData || [])
    } catch (err) {
      console.error('AgentDashboard fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [user, organization, isSuperAdmin])

  useEffect(() => {
    fetchDashboard()
    const interval = setInterval(fetchDashboard, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [fetchDashboard])

  // ─── THEME ───────────────────────────────────────────────────
  const C = darkMode ? {
    bg:         '#0a1628',
    bgGrid:     'rgba(116,167,220,0.055)',
    surface:    '#122036',
    surface2:   '#162540',
    surface3:   '#1a2a4a',
    border:     'rgba(125,170,218,0.18)',
    borderStrong:'rgba(125,170,218,0.32)',
    text:       '#edf5ff',
    muted:      '#91a9c2',
    dim:        '#5f7893',
    accent:     '#3d6494',
    green:      '#37d67a',
    amber:      '#ffbd4a',
    red:        '#ff5f6d',
    blue:       '#6da8ff',
    shadow:     '0 22px 60px rgba(0,0,0,0.28)',
  } : {
    bg:         '#eef4fa',
    bgGrid:     'rgba(28,77,125,0.06)',
    surface:    '#ffffff',
    surface2:   '#f5f9fd',
    surface3:   '#edf4fb',
    border:     'rgba(54,93,130,0.16)',
    borderStrong:'rgba(54,93,130,0.28)',
    text:       '#102033',
    muted:      '#506b87',
    dim:        '#7891aa',
    accent:     '#3d6494',
    green:      '#10884a',
    amber:      '#b46904',
    red:        '#c9283c',
    blue:       '#216fd2',
    shadow:     '0 18px 50px rgba(31,66,104,0.12)',
  }

  // Animated counters — must be called before any early returns
  const animTotal   = useAnimatedCounter(liveOps.myTotal)
  const animCurrent = useAnimatedCounter(statCards.current)
  const animDueSoon = useAnimatedCounter(statCards.dueSoon)
  const animOverdue = useAnimatedCounter(statCards.overdue)
  const animMonth   = useAnimatedCounter(statCards.myMonth)

  const panelStyle = {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    boxShadow: C.shadow,
    overflow: 'hidden',
  }

  function chip(color, label) {
    return (
      <span style={{
        display: 'inline-block', padding: '4px 10px', borderRadius: 999,
        fontSize: 11, fontWeight: 700,
        border: `1px solid ${color}`,
        background: `color-mix(in srgb, ${color}, transparent 88%)`,
        color, whiteSpace: 'nowrap',
      }}>
        {label}
      </span>
    )
  }

  function PanelHeader({ kicker, title, action, actionLabel }) {
    return (
      <div style={{ padding: '15px 16px', borderBottom: `1px solid ${C.border}`, background: C.surface2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ color: C.dim, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>{kicker}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{title}</div>
        </div>
        {actionLabel && (
          <button
            onClick={action}
            style={{ border: `1px solid ${C.border}`, background: C.surface2, color: C.muted, borderRadius: 6, height: 30, padding: '0 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            {actionLabel}
          </button>
        )}
      </div>
    )
  }


  if (loading) {
    return (
      <div style={{ minHeight: '100%', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}>
        Loading…
      </div>
    )
  }

  const anim = (delay) => ({
    animation: `dashFadeUp 0.4s ease-out both`,
    animationDelay: `${delay}ms`,
  })

  return (
    <>
    <style>{`
      @keyframes dashFadeUp {
        from { opacity: 0; transform: translateY(10px); }
        to   { opacity: 1; transform: translateY(0); }
      }
    `}</style>
    <div style={{
      minHeight: '100%',
      background: 'transparent',
      color: C.text,
      fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
    }}>
      <div style={{ padding: 26, display: 'grid', gap: 18 }}>

        {/* ─── METRIC CARDS ─── */}
        <div style={{ ...panelStyle, display: 'flex', ...anim(0) }}>
          {[
            { label: 'Total Assigned', value: animTotal,   color: C.accent, note: 'My establishments' },
            { label: 'Current',        value: animCurrent, color: C.green,  note: 'Active MXB coverage' },
            { label: 'Due Soon',       value: animDueSoon, color: C.amber,  note: 'Next 30–180 days' },
            { label: 'Overdue',        value: animOverdue, color: C.red,    note: 'Past 365 days' },
            { label: 'My Month',       value: animMonth,   color: C.blue,   note: 'Inspections conducted' },
          ].map((m, i, arr) => (
            <div key={i} style={{
              flex: 1, padding: '16px 20px',
              borderRight: i < arr.length - 1 ? `1px solid ${C.border}` : 'none',
              position: 'relative',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: m.color, borderRadius: '8px 0 0 8px' }} />
              <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase' }}>{m.label}</div>
              <div style={{ fontSize: 28, lineHeight: 1, fontWeight: 700, letterSpacing: '-0.03em', color: C.text }}>{m.value}</div>
              <div style={{ fontSize: 11, color: C.dim }}>{m.note}</div>
            </div>
          ))}
        </div>

        {/* ─── ON CALL + FOLLOW-UPS STRIPS ─── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, ...anim(40) }}>

          {/* On Call — left half */}
          <div style={{ ...panelStyle, display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted, flexShrink: 0 }}>On Call Tonight</div>
            <div style={{ width: 1, height: 14, background: C.border, flexShrink: 0 }} />
            {onCallAgent ? (
              <>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: C.green, flexShrink: 0 }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{onCallAgent.first_name} {onCallAgent.last_name}</div>
                {onCallAgent.phone_number && (
                  <div style={{ fontSize: 12, color: C.dim }}>{onCallAgent.phone_number}</div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 12, color: C.dim }}>No agent currently on call</div>
            )}
          </div>

          {/* Follow-ups — right half */}
          <div
            onClick={() => setShowFollowups(true)}
            style={{ ...panelStyle, display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', cursor: 'pointer', transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}
            onMouseLeave={e => e.currentTarget.style.background = C.surface}
          >
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted, flexShrink: 0 }}>Follow-ups</div>
            <div style={{ width: 1, height: 14, background: C.border, flexShrink: 0 }} />
            <div style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: 22, height: 22, borderRadius: 11,
              background: C.accent,
              color: '#fff',
              fontSize: 11, fontWeight: 700, padding: '0 6px',
            }}>
              {followups.length}
            </div>
            {followups.length > 0 ? (
              <div style={{ fontSize: 12, color: C.dim }}>
                {followups.length === 1 ? '1 pending' : `${followups.length} pending`}
                {followups[0]?.followup_date ? ` · next due ${formatDate(followups[0].followup_date)}` : ''}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: C.dim }}>No pending follow-ups</div>
            )}
          </div>

        </div>

        {/* ─── MAIN LAYOUT ─── */}
        {/* 2-col: queue left, right side is a flex column so map naturally fills top portion */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,2fr)', gap: 18, alignItems: 'stretch' }}>

          {/* Col 1: Queue */}
          <div style={{ ...panelStyle, display: 'flex', flexDirection: 'column', ...anim(160) }}>
            <PanelHeader
              kicker="Attention queue"
              title="What to inspect next"
              action={() => onNavigate && onNavigate('main')}
              actionLabel="View All"
            />
            {attentionQueue.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: C.muted, fontSize: 14 }}>
                No establishments assigned.
              </div>
            ) : (
              <div style={{ overflowY: 'auto' }}>
                {attentionQueue.slice(0, 10).map((est, i) => (
                  <div
                    key={est.id}
                    onClick={() => onNavigate && onNavigate('main')}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '38px minmax(0,1fr) 84px 76px',
                      alignItems: 'center',
                      gap: 10,
                      padding: '9px 16px',
                      borderBottom: i < Math.min(attentionQueue.length, 10) - 1 ? `1px solid ${C.border}` : 'none',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: 6,
                      display: 'grid', placeItems: 'center',
                      background: C.surface2, color: C.accent,
                      fontSize: 12, fontWeight: 700,
                      border: `1px solid ${C.border}`,
                    }}>
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {est.trade_name}
                      </div>
                      <div style={{ marginTop: 2, color: C.dim, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {est.mxb_number} · {est.lastDate ? `last checked ${formatDate(est.lastDate)}` : 'never inspected'}
                      </div>
                    </div>
                    <div>
                      {est.status === 'overdue'  && chip(C.red,   'Overdue')}
                      {est.status === 'dueSoon'  && chip(C.amber, 'Due Soon')}
                      {est.status === 'current'  && chip(C.green, 'Current')}
                      {est.status === 'never'    && chip(C.dim,   'Never')}
                    </div>
                    <div>
                      <button
                        onClick={e => { e.stopPropagation(); setInspectingEst(est) }}
                        style={{
                          height: 28, padding: '0 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                          cursor: 'pointer', whiteSpace: 'nowrap',
                          background: C.accent, color: '#fff',
                          border: 'none', transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                      >
                        Inspect
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Col 2: flex column — map fills top, pulse+complaints sit at bottom */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, ...anim(220) }}>

            {/* Map — flex:1 so it fills everything above the bottom row */}
            <div style={{ ...panelStyle, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
              <PanelHeader
                kicker="Territory"
                title="My establishments"
                action={() => onNavigate && onNavigate('main')}
                actionLabel="View All"
              />
              <div style={{ flex: 1, minHeight: 0 }}>
                <TerritoryMap establishments={mapEstablishments} C={C} darkMode={darkMode} onNavigate={onNavigate} mapHeight="100%" />
              </div>
            </div>

            {/* Bottom row: Pulse + Complaints side by side */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>

              {/* Pulse */}
              <div style={{ ...panelStyle, display: 'flex', flexDirection: 'column' }}>
                <PanelHeader kicker="Pulse" title="Recent activity" />
                <div style={{ padding: '12px 16px' }}>
                  {recentActivity.length === 0 ? (
                    <div style={{ color: C.muted, fontSize: 13 }}>No recent inspections.</div>
                  ) : (
                    recentActivity.map((insp, i) => {
                      const dotColor = insp.disposition === 'in_compliance' ? C.green
                        : insp.disposition === 'violation' ? C.red
                        : C.amber
                      return (
                        <div key={insp.id} style={{
                          display: 'grid', gridTemplateColumns: '24px minmax(0,1fr)', gap: 10, alignItems: 'start',
                          padding: '8px 0',
                          borderBottom: i < recentActivity.length - 1 ? `1px solid ${C.border}` : 'none',
                        }}>
                          <div style={{
                            marginTop: 4, width: 10, height: 10, borderRadius: '50%',
                            background: dotColor,
                            boxShadow: `0 0 0 3px color-mix(in srgb, ${dotColor}, transparent 82%)`,
                          }} />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {insp.establishments?.trade_name || '—'}
                            </div>
                            <div style={{ marginTop: 2, color: C.dim, fontSize: 12 }}>
                              {insp.users ? `${insp.users.first_name} ${insp.users.last_name}` : 'Unknown'} · {formatDate(insp.inspection_date)}
                            </div>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>

              {/* Complaints / Larceny */}
              {!organization?.features?.larceny_reports ? (
                <div style={panelStyle}>
                  <PanelHeader kicker="Assigned to me" title="My complaints" action={() => onNavigate && onNavigate('complaints')} actionLabel="View All" />
                  <div style={{ padding: '12px 16px' }}>
                    {complaints.length === 0 ? (
                      <div style={{ color: C.muted, fontSize: 13 }}>No open complaints assigned to you.</div>
                    ) : (
                      complaints.slice(0, 5).map((c, i) => (
                        <div
                          key={c.id}
                          onClick={() => onNavigate && onNavigate('complaints')}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                            cursor: 'pointer', padding: '8px 2px',
                            borderBottom: i < Math.min(complaints.length, 5) - 1 ? `1px solid ${C.border}` : 'none',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {c.establishments?.trade_name || 'No establishment'}
                            </div>
                            <div style={{ fontSize: 12, color: C.dim, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {c.complaint_description || '—'} · {formatDate(c.date_received)}
                            </div>
                          </div>
                          <div style={{ flexShrink: 0 }}>
                            {chip(
                              c.status === 'new' ? C.green : c.status === 'open' ? C.red : C.amber,
                              c.status === 'new' ? 'New' : c.status === 'open' ? 'Open' : c.status === 'investigating' ? 'Investigating' : c.status === 'resolved' ? 'Resolved' : c.status
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                <div style={panelStyle}>
                  <PanelHeader kicker="Store Reports" title="Recent larceny reports" action={() => onNavigate && onNavigate('larceny')} actionLabel="View All" />
                  <div style={{ padding: '12px 16px' }}>
                    {larcenyReports.length === 0 ? (
                      <div style={{ color: C.muted, fontSize: 13 }}>No recent reports.</div>
                    ) : (
                      larcenyReports.map((r, i) => {
                        const statusColor = r.status === 'closed' ? C.green : r.status === 'pending' ? C.blue : C.amber
                        const statusLabel = r.status === 'closed' ? 'Closed' : r.status === 'pending' ? 'Pending' : 'New'
                        const dt = new Date(r.created_at)
                        const dateStr = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                        const timeStr = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                        return (
                          <div key={r.id} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                            padding: '8px 2px',
                            borderBottom: i < larcenyReports.length - 1 ? `1px solid ${C.border}` : 'none',
                          }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                                {r.abc_stores?.store_number ? `Store #${r.abc_stores.store_number}` : 'Unknown Store'}
                              </div>
                              <div style={{ fontSize: 12, color: C.dim, marginTop: 2 }}>{dateStr} · {timeStr}</div>
                            </div>
                            <div style={{ flexShrink: 0 }}>{chip(statusColor, statusLabel)}</div>
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>

        </div>

      </div>
    </div>

    {/* ─── INSPECTION MODAL ─── */}
    {inspectingEst && (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(5,12,24,0.72)',
          backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          overflowY: 'auto', padding: '24px 16px',
        }}
      >
        <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 860 }}>
          <Suspense fallback={
            <div style={{ background: C.surface, borderRadius: 10, padding: 40, textAlign: 'center', color: C.muted, fontSize: 14 }}>
              Loading inspection form…
            </div>
          }>
            <InspectionForm
              preSelectedEstablishment={inspectingEst}
              onClose={() => setInspectingEst(null)}
              onSuccess={() => { setInspectingEst(null); fetchDashboard() }}
            />
          </Suspense>
        </div>
      </div>
    )}

    {/* ─── FOLLOW-UPS MODAL ─── */}
    {showFollowups && (
      <div onClick={() => setShowFollowups(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div onClick={e => e.stopPropagation()} style={{ background: darkMode ? '#111f33' : '#fff', border: `1px solid ${C.border}`, borderRadius: 10, width: '100%', maxWidth: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, background: C.surface2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.dim, marginBottom: 3 }}>Reminders</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>Follow-ups required</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ minWidth: 24, height: 24, borderRadius: 12, background: C.accent, color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px' }}>
                {followups.length}
              </div>
              <button onClick={() => setShowFollowups(false)} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', fontSize: 12, color: C.dim, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
          <div style={{ overflowY: 'auto', padding: '8px 0' }}>
            {followups.length === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: C.muted, fontSize: 13 }}>No pending follow-ups.</div>
            ) : (
              followups.map((f, i) => {
                const isOverdue = f.followup_date && daysSince(f.followup_date) > 0
                return (
                  <div key={f.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                    padding: '10px 20px',
                    borderBottom: i < followups.length - 1 ? `1px solid ${C.border}` : 'none',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.establishments?.trade_name || '—'}
                      </div>
                      <div style={{ fontSize: 12, color: C.dim, marginTop: 2 }}>
                        {f.establishments?.mxb_number}
                        {f.followup_notes ? ` · ${f.followup_notes}` : ''}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      {chip(isOverdue ? C.red : C.amber, f.followup_date ? formatDate(f.followup_date) : '—')}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    )}

</>
  )
}
